import { useState } from "react";
import { toast } from "sonner";
import { useSuppliers } from "../../hooks/useSuppliers";
import { useReceiptInputs } from "../../hooks/useInputs";
import type { Input as InventoryInput } from "../../services/inputsApi";
import { useCreateBatchesWithReceipts } from "../../hooks/useStockReceipt";
import type { CreateBatchRequest } from "../../services/stockReceiptApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Field, FieldLabel } from "@/components/ui/field";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
} from "@/components/ui/combobox";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Trash2, Plus } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useUnitsOfMeasure } from "@/shared/hooks/useUnitsOfMeasure";
import type { AxiosError } from "axios";
import { needsReceiptUnit, resolveReceiptUnit } from "./receiptUnit";

interface BatchItem extends CreateBatchRequest {
    tempId: string;
    inputLabel: string;
    packsReceived?: number;
    packSize?: number;
    seedCount?: number;
    // Keep the resolved unit with the line so submission does not depend on a
    // later Inputs lookup still containing this input.
    receiptUnit?: string;
    // Supports lines added before the current form version was loaded.
    unitOverride?: string;
}

// Receipt-level fields (entered once for the whole delivery).
// Per SPEC_DEMO2FEEDBACK_STOCK_INPUTS_OVERHAUL §d/§e, the "Reference Number" and
// "Requires Approval" fields are removed — every receipt is GM-approved through
// the new workflow, and reference data is captured via PO Number / Invoice #.
interface ReceiptHeader {
    supplierId: string;
    invoiceNumber: string;
    purchaseOrderNumber: string;
    receivedDate: string;
    movementDate: string;
    notes: string;
}

// Line-item fields (entered per item in the delivery)
interface LineItemForm {
    inputId: string;
    packsReceived: string;
    packSizePerPack: string;
    seedCount: string;  // Total seeds from packet label (seed inputs only)
    manufacturerBatchNumber: string;
    lotNumber: string;
    receivedQuantity: string;
    manufactureDate: string;
    expiryDate: string;
    unitCost: string;
    // Only used (and only required) when the chosen input has no native unit
    // yet. The first receipt establishes the unit for the catalog row.
    unitOfMeasure: string;
}

const initialHeader: ReceiptHeader = {
    supplierId: "",
    invoiceNumber: "",
    purchaseOrderNumber: "",
    receivedDate: new Date().toISOString().split("T")[0],
    movementDate: new Date().toISOString().split("T")[0],
    notes: "",
};

const initialLineItem: LineItemForm = {
    inputId: "",
    packsReceived: "",
    packSizePerPack: "",
    seedCount: "",
    manufacturerBatchNumber: "",
    lotNumber: "",
    receivedQuantity: "",
    manufactureDate: "",
    expiryDate: "",
    unitCost: "",
    unitOfMeasure: "",
};

export const StockReceiptForm = () => {
    const [header, setHeader] = useState<ReceiptHeader>(initialHeader);
    const [lineItem, setLineItem] = useState<LineItemForm>(initialLineItem);
    const [addedBatches, setAddedBatches] = useState<BatchItem[]>([]);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
    const [inputSearchValue, setInputSearchValue] = useState("");
    const [inputSearchTerm, setInputSearchTerm] = useState("");
    const [chosenInput, setChosenInput] = useState<InventoryInput | null>(null);

    const suppliersQuery = useSuppliers();
    const inputsQuery = useReceiptInputs(header.supplierId, inputSearchTerm);
    const unitsQuery = useUnitsOfMeasure();
    const createReceiptMutation = useCreateBatchesWithReceipts();

    const filteredInputs = inputsQuery.data?.pages.flatMap((page) => page.items) ?? [];
    const selectedInput = chosenInput?.id === lineItem.inputId ? chosenInput : null;

    // Heuristic for "seed-related" Inputs (SPEC §b/§f): the chosen InputType's
    // name contains "seed" (case-insensitive). These are the rows where Variety
    // must be set on the catalogue before receiving stock.
    const isSeedInput =
        !!selectedInput?.inputTypeName &&
        selectedInput.inputTypeName.toLowerCase().includes("seed");

    // Pack calculation only makes sense for bulk/continuous units (kg, g, L, mL,
    // seed count) where "3 packs × 500g = 1500g". For discrete units like packets,
    // bags, boxes etc. the quantity IS the packs count — size per pack is metadata.
    const DISCRETE_UNITS = ["pieces", "piece", "boxes", "box", "bags", "bag", "packets", "packet", "bottles", "bottle", "cans", "can", "trays", "tray"];
    const selectedUnit = selectedInput
        ? resolveReceiptUnit(selectedInput.unitOfMeasure, lineItem.unitOfMeasure)
        : "";
    const isDiscreteUnit = !!selectedUnit && DISCRETE_UNITS.includes(selectedUnit.toLowerCase());

    const parsedPacksReceived = parseFloat(lineItem.packsReceived);
    const parsedPackSizePerPack = parseFloat(lineItem.packSizePerPack);

    // For bulk units: quantity = packs × size. For discrete: quantity = packs count.
    const hasPackCalculationInputs = !Number.isNaN(parsedPacksReceived) && parsedPacksReceived > 0 && (
        isDiscreteUnit || (!Number.isNaN(parsedPackSizePerPack) && parsedPackSizePerPack > 0)
    );
    const calculatedQuantity = hasPackCalculationInputs
        ? isDiscreteUnit
            ? parsedPacksReceived
            : Number((parsedPacksReceived * parsedPackSizePerPack).toFixed(4))
        : null;

    const handleHeaderChange = (field: keyof ReceiptHeader, value: string) => {
        setHeader((prev) => ({ ...prev, [field]: value }));
        if (fieldErrors[field]) {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };

    const handleLineItemChange = (field: keyof LineItemForm, value: string) => {
        const inputData = field === "inputId"
            ? filteredInputs.find((input) => input.id === value)
            : undefined;
        if (field === "inputId") setChosenInput(inputData ?? null);
        setLineItem((prev) => {
            const updated = { ...prev, [field]: value };

            if (field === "inputId") {
                updated.packsReceived = "";
                updated.packSizePerPack = "";
                updated.receivedQuantity = "";
                updated.unitOfMeasure = "";
                if (inputData) {
                    updated.unitCost = inputData.unitCost?.toString() ?? "";
                    if (inputData.hasExpiryDate && inputData.shelfLifeDays && header.receivedDate) {
                        const expiry = new Date(header.receivedDate);
                        if (!Number.isNaN(expiry.getTime())) {
                            expiry.setDate(expiry.getDate() + inputData.shelfLifeDays);
                            updated.expiryDate = expiry.toISOString().split("T")[0];
                        }
                    }
                }
            }

            return updated;
        });

        if (fieldErrors[field]) {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };

    const handlePacksReceivedChange = (value: string) => {
        setLineItem((prev) => {
            const updated = { ...prev, packsReceived: value };
            const packs = parseFloat(value);
            const sizePerPack = parseFloat(updated.packSizePerPack);

            if (isDiscreteUnit && !Number.isNaN(packs) && packs > 0) {
                // Discrete units: quantity = packs count
                updated.receivedQuantity = packs.toString();
            } else if (!Number.isNaN(packs) && packs > 0 && !Number.isNaN(sizePerPack) && sizePerPack > 0) {
                updated.receivedQuantity = Number((packs * sizePerPack).toFixed(4)).toString();
            }

            return updated;
        });

        if (fieldErrors.packsReceived) {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next.packsReceived;
                return next;
            });
        }
    };

    const handlePackSizePerPackChange = (value: string) => {
        setLineItem((prev) => {
            const updated = { ...prev, packSizePerPack: value };
            const packs = parseFloat(updated.packsReceived);
            const sizePerPack = parseFloat(value);

            // For discrete units, size per pack is metadata — doesn't change quantity
            if (!isDiscreteUnit && !Number.isNaN(packs) && packs > 0 && !Number.isNaN(sizePerPack) && sizePerPack > 0) {
                updated.receivedQuantity = Number((packs * sizePerPack).toFixed(4)).toString();
            }

            return updated;
        });
    };

    const validateHeader = (): boolean => {
        const errors: Record<string, string[]> = {};
        if (!header.supplierId) errors.supplierId = ["This field is required"];
        if (!header.invoiceNumber) errors.invoiceNumber = ["This field is required"];
        if (!header.purchaseOrderNumber) errors.purchaseOrderNumber = ["This field is required"];
        if (!header.receivedDate) errors.receivedDate = ["This field is required"];

        if (Object.keys(errors).length > 0) {
            setFieldErrors((prev) => ({ ...prev, ...errors }));
            return false;
        }
        return true;
    };

    const validateLineItem = (): boolean => {
        const errors: Record<string, string[]> = {};
        if (!lineItem.inputId) errors.inputId = ["This field is required"];
        if (lineItem.inputId && !selectedInput) {
            errors.inputId = ["Input details are unavailable. Re-select this Input."];
        }
        if (!lineItem.manufacturerBatchNumber) errors.manufacturerBatchNumber = ["This field is required"];
        if (!lineItem.lotNumber) errors.lotNumber = ["This field is required"];
        if (Number.isNaN(parsedPacksReceived) || parsedPacksReceived <= 0) {
            errors.packsReceived = ["Packs received is required for supplier invoice costing"];
        }
        const effectiveQuantity = hasPackCalculationInputs
            ? (calculatedQuantity ?? 0)
            : parseFloat(lineItem.receivedQuantity);

        if (!effectiveQuantity || effectiveQuantity <= 0) {
            errors.receivedQuantity = ["Quantity must be greater than zero"];
        }
        if (!lineItem.manufactureDate) errors.manufactureDate = ["This field is required"];
        if (!lineItem.unitCost) errors.unitCost = ["Cost per pack is required"];

        // Expiry date is required only if the input has expiry dates
        if (selectedInput?.hasExpiryDate && !lineItem.expiryDate) {
            errors.expiryDate = ["This field is required"];
        }

        // SPEC §b/§f: seed-type Inputs must carry a Variety on the catalogue before
        // receipt. Block the line with the spec's wording so the operator goes back
        // and fills it in on the Input rather than guessing here.
        if (isSeedInput && !selectedInput?.variety?.trim()) {
            errors.variety = [
                "This seed Input has no variety set. Configure the variety on the Input catalogue first.",
            ];
        }

        // First-receipt-wins: if the input was created without a unit, the
        // receiver must specify one here — it'll become the catalog's native unit.
        if (selectedInput && needsReceiptUnit(selectedInput.unitOfMeasure) && !lineItem.unitOfMeasure) {
            errors.unitOfMeasure = [
                "Pick a unit — this is the first receipt for this Input and will set its native unit.",
            ];
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors((prev) => ({ ...prev, ...errors }));
            return false;
        }
        return true;
    };

    const handleAddLineItem = () => {
        if (!validateHeader() || !validateLineItem()) {
            return;
        }

        const effectiveQuantity = hasPackCalculationInputs
            ? (calculatedQuantity ?? 0)
            : parseFloat(lineItem.receivedQuantity);

        const newBatch: BatchItem = {
            tempId: Date.now().toString(),
            inputLabel: selectedInput?.variety
                ? `${selectedInput.inputName} — ${selectedInput.variety}`
                : selectedInput?.inputName ?? "",
            inputId: lineItem.inputId,
            supplierId: header.supplierId || undefined,
            batchNumber: "",
            manufacturerBatchNumber: lineItem.manufacturerBatchNumber,
            lotNumber: lineItem.lotNumber,
            receivedQuantity: effectiveQuantity,
            receivedDate: header.receivedDate,
            manufactureDate: lineItem.manufactureDate,
            expiryDate: lineItem.expiryDate || null,
            unitCost: parseFloat(lineItem.unitCost),
            commercialQuantity: lineItem.packsReceived ? parseFloat(lineItem.packsReceived) : undefined,
            commercialUnit: "Pack",
            commercialUnitCost: parseFloat(lineItem.unitCost),
            purchaseOrderNumber: header.purchaseOrderNumber,
            invoiceNumber: header.invoiceNumber,
            numberOfPacks: lineItem.packsReceived ? parseFloat(lineItem.packsReceived) : undefined,
            packSize: lineItem.packSizePerPack ? parseFloat(lineItem.packSizePerPack) : undefined,
            seedCount: lineItem.seedCount ? parseInt(lineItem.seedCount, 10) : undefined,
            packsReceived: lineItem.packsReceived ? parseFloat(lineItem.packsReceived) : undefined,
            receiptUnit: selectedUnit,
        };

        setAddedBatches([...addedBatches, newBatch]);
        setLineItem(initialLineItem);
        setChosenInput(null);
        setInputSearchValue("");
        setInputSearchTerm("");
        setFieldErrors({});
    };

    const handleRemoveBatch = (tempId: string) => {
        setAddedBatches(addedBatches.filter((b) => b.tempId !== tempId));
    };

    const handleSubmit = () => {
        if (addedBatches.length === 0) {
            toast.error("Please add at least one line item");
            return;
        }

        if (!header.movementDate) {
            toast.error("Please fill movement date.");
            return;
        }

        // Validate every line before creating any batches. New lines carry the
        // unit resolved when added; the lookup is only for older in-memory lines.
        const lines = addedBatches.map((batch) => {
            const unit = batch.receiptUnit || batch.unitOverride || "";
            const payload: Partial<BatchItem> = { ...batch };
            delete payload.tempId;
            delete payload.receiptUnit;
            delete payload.unitOverride;
            delete payload.inputLabel;
            return { batch: payload as CreateBatchRequest, unit };
        });

        if (lines.some((line) => !line.unit)) {
            toast.error("Input details are unavailable for an added line. Remove and re-add it.");
            return;
        }

        if (lines.some((line) => needsReceiptUnit(line.unit))) {
            toast.error("Specify a unit for this Input on the line — it has no native unit yet.");
            return;
        }

        const unitsByInput = new Map<string, string>();
        for (const line of lines) {
            const previousUnit = unitsByInput.get(line.batch.inputId);
            if (previousUnit && previousUnit !== line.unit) {
                toast.error("Lines for the same Input must use the same unit.");
                return;
            }
            unitsByInput.set(line.batch.inputId, line.unit);
        }

        createReceiptMutation.mutate(
            {
                lines,
                movementDate: header.movementDate,
                notes: header.notes,
            },
            {
                onSuccess: () => {
                    toast.success(
                        "Stock receipt recorded — pending GM approval before stock becomes drawable."
                    );
                    setAddedBatches([]);
                    setHeader(initialHeader);
                    setLineItem(initialLineItem);
                    setChosenInput(null);
                    setInputSearchValue("");
                    setInputSearchTerm("");
                },
                onError: (error: unknown) => {
                    const response = (error as AxiosError<{
                        errors?: Record<string, string[]>;
                        message?: string;
                    }>).response?.data;
                    const validationErrors = response?.errors;
                    const errorMessage = response?.message || "Failed to record stock receipt";

                    if (validationErrors) {
                        setFieldErrors(validationErrors);
                        const errorMessages = Object.entries(validationErrors)
                            .flatMap(([, messages]) => messages);

                        if (errorMessages.length > 0) {
                            toast.error(errorMessages.join("\n"));
                            return;
                        }
                    }

                    toast.error(errorMessage);
                },
            }
        );
    };

    const isPending = createReceiptMutation.isPending;

    return (
        <div className="space-y-6">
            {/* Receipt Header */}
            <div className="rounded-lg border p-6 space-y-6">
                <h2 className="text-xl font-semibold">Receipt Details</h2>
                <p className="text-sm text-muted-foreground">
                    Enter the delivery/invoice information — this applies to all items below.
                </p>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
                    <Field>
                        <FieldLabel htmlFor="supplierId">
                            Supplier <span className="text-red-500">*</span>
                        </FieldLabel>
                        {suppliersQuery.isLoading ? (
                            <div className="h-10 bg-gray-200 rounded animate-pulse" />
                        ) : (
                            <Select
                                value={header.supplierId}
                                onValueChange={(value) => {
                                    const cleared = value === "__clear__" ? "" : value;
                                    handleHeaderChange("supplierId", cleared);
                                    setLineItem((prev) => ({ ...prev, inputId: "" }));
                                    setChosenInput(null);
                                    setInputSearchValue("");
                                    setInputSearchTerm("");
                                }}
                                disabled={isPending || addedBatches.length > 0}
                            >
                                <SelectTrigger id="supplierId" className="w-full">
                                    <SelectValue placeholder="Select Supplier" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__clear__" className="text-muted-foreground">
                                        — None —
                                    </SelectItem>
                                    {suppliersQuery.data?.map((supplier) => (
                                        <SelectItem key={supplier.id} value={supplier.id}>
                                            {supplier.supplierName}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        {addedBatches.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-1">
                                Locked — remove all items to change supplier
                            </p>
                        )}
                        {fieldErrors.supplierId && (
                            <div className="text-red-500 text-xs mt-1">
                                {fieldErrors.supplierId.join(", ")}
                            </div>
                        )}
                    </Field>

                    <Field>
                        <FieldLabel htmlFor="invoiceNumber">
                            Supplier Invoice # <span className="text-red-500">*</span>
                        </FieldLabel>
                        <Input
                            id="invoiceNumber"
                            value={header.invoiceNumber}
                            onChange={(e) => handleHeaderChange("invoiceNumber", e.target.value)}
                            disabled={isPending}
                            placeholder="e.g. INV-2026-001"
                        />
                        {fieldErrors.invoiceNumber && (
                            <div className="text-red-500 text-xs mt-1">
                                {fieldErrors.invoiceNumber.join(", ")}
                            </div>
                        )}
                    </Field>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Field>
                        <FieldLabel htmlFor="purchaseOrderNumber">
                            PO Number <span className="text-red-500">*</span>
                        </FieldLabel>
                        <Input
                            id="purchaseOrderNumber"
                            value={header.purchaseOrderNumber}
                            onChange={(e) => handleHeaderChange("purchaseOrderNumber", e.target.value)}
                            disabled={isPending}
                            placeholder="e.g. PO-2026-001"
                        />
                        {fieldErrors.purchaseOrderNumber && (
                            <div className="text-red-500 text-xs mt-1">
                                {fieldErrors.purchaseOrderNumber.join(", ")}
                            </div>
                        )}
                    </Field>

                    <Field>
                        <FieldLabel htmlFor="receivedDate">
                            Received Date <span className="text-red-500">*</span>
                        </FieldLabel>
                        <DatePicker
                            id="receivedDate"
                            value={header.receivedDate}
                            onChange={(value) => handleHeaderChange("receivedDate", value)}
                            disabled={isPending}
                        />
                        {fieldErrors.receivedDate && (
                            <div className="text-red-500 text-xs mt-1">
                                {fieldErrors.receivedDate.join(", ")}
                            </div>
                        )}
                    </Field>

                    <Field>
                        <FieldLabel htmlFor="movementDate">
                            Movement Date <span className="text-red-500">*</span>
                        </FieldLabel>
                        <DatePicker
                            id="movementDate"
                            value={header.movementDate}
                            onChange={(value) => handleHeaderChange("movementDate", value)}
                            disabled={isPending}
                        />
                    </Field>
                </div>

                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    Every stock receipt is recorded as <strong>pending</strong> and must be
                    approved by the General Manager before the stock becomes drawable.
                </p>
            </div>

            {/* Line Items Section */}
            <div className="rounded-lg border p-6 space-y-6">
                <h2 className="text-xl font-semibold">Line Items</h2>
                <p className="text-sm text-muted-foreground">
                    Add each item from this delivery. All items share the same invoice and PO above.
                </p>

                {/* Line Item Entry Form */}
                <div className="rounded-md border border-dashed p-4 space-y-4">
                    <div className="grid grid-cols-5 gap-4">
                        <Field>
                            <FieldLabel htmlFor="inputId">
                                Input <span className="text-red-500">*</span>
                            </FieldLabel>
                            <Combobox
                                filter={null}
                                value={lineItem.inputId || null}
                                onValueChange={(value) => {
                                    handleLineItemChange("inputId", value ?? "");
                                    if (value) {
                                        const chosen = filteredInputs.find(i => i.id === value);
                                        setInputSearchValue(chosen
                                            ? chosen.variety ? `${chosen.inputName} — ${chosen.variety}` : chosen.inputName
                                            : "");
                                    } else {
                                        setInputSearchValue("");
                                        setInputSearchTerm("");
                                    }
                                }}
                                inputValue={inputSearchValue}
                                disabled={!header.supplierId || isPending}
                            >
                                <ComboboxInput
                                    placeholder={header.supplierId ? "Search input..." : "Select Supplier First"}
                                    showClear={!!lineItem.inputId}
                                    className="w-full"
                                    onChange={(e) => {
                                        if (lineItem.inputId) handleLineItemChange("inputId", "");
                                        setInputSearchValue(e.target.value);
                                        setInputSearchTerm(e.target.value.trim());
                                    }}
                                />
                                <ComboboxContent>
                                    <ComboboxList>
                                        {filteredInputs.length === 0 && (
                                            <ComboboxEmpty>{inputsQuery.isFetching ? "Loading inputs..." : "No inputs found."}</ComboboxEmpty>
                                        )}
                                        {filteredInputs.map((input) => (
                                            <ComboboxItem key={input.id} value={input.id}>
                                                {input.variety
                                                    ? `${input.inputName} — ${input.variety}`
                                                    : input.inputName}
                                            </ComboboxItem>
                                        ))}
                                        {inputsQuery.hasNextPage && (
                                            <Button type="button" variant="ghost" className="w-full" disabled={inputsQuery.isFetchingNextPage} onClick={() => inputsQuery.fetchNextPage()}>
                                                {inputsQuery.isFetchingNextPage ? "Loading..." : "Load more inputs"}
                                            </Button>
                                        )}
                                    </ComboboxList>
                                </ComboboxContent>
                            </Combobox>
                            {fieldErrors.inputId && (
                                <div className="text-red-500 text-xs mt-1">
                                    {fieldErrors.inputId.join(", ")}
                                </div>
                            )}
                            {selectedInput && (
                                <p className="text-xs text-muted-foreground mt-1">
                                    Variety:{" "}
                                    {selectedInput.variety
                                        ? <span className="font-medium text-foreground">{selectedInput.variety}</span>
                                        : isSeedInput
                                            ? <span className="text-red-600">none configured (required for seed inputs)</span>
                                            : <span className="text-muted-foreground">none</span>}
                                </p>
                            )}
                            {fieldErrors.variety && (
                                <div className="text-red-500 text-xs mt-1">
                                    {fieldErrors.variety.join(", ")}
                                </div>
                            )}
                        </Field>

                        <Field>
                            <FieldLabel htmlFor="packsReceived">
                                Packs Received <span className="text-red-500">*</span>
                            </FieldLabel>
                            <Input
                                id="packsReceived"
                                type="number"
                                step="0.01"
                                value={lineItem.packsReceived}
                                onChange={(e) => handlePacksReceivedChange(e.target.value)}
                                disabled={isPending}
                                placeholder="e.g. 3"
                            />
                            {fieldErrors.packsReceived && (
                                <div className="text-red-500 text-xs mt-1">
                                    {fieldErrors.packsReceived.join(", ")}
                                </div>
                            )}
                        </Field>

                        <Field>
                            <FieldLabel htmlFor="packSizePerPack">
                                {isDiscreteUnit ? "Seeds Per Pack" : `Size Per Pack ${selectedInput ? `(${selectedInput.unitOfMeasureDisplay || selectedInput.unitOfMeasure})` : ""}`}
                            </FieldLabel>
                            <Input
                                id="packSizePerPack"
                                type="number"
                                step="0.01"
                                value={lineItem.packSizePerPack}
                                onChange={(e) => handlePackSizePerPackChange(e.target.value)}
                                disabled={isPending}
                                placeholder={isDiscreteUnit ? "e.g. 2500" : "e.g. 100 or 25"}
                            />
                            {isDiscreteUnit && (
                                <div className="text-xs text-muted-foreground mt-1">
                                    Informational — does not affect quantity
                                </div>
                            )}
                        </Field>

                        <Field>
                            <FieldLabel htmlFor="seedCount">
                                Seed Count (from packet)
                            </FieldLabel>
                            <Input
                                id="seedCount"
                                type="number"
                                step="1"
                                min="0"
                                value={lineItem.seedCount}
                                onChange={(e) => handleLineItemChange("seedCount", e.target.value)}
                                disabled={isPending}
                                placeholder="e.g. 25000"
                            />
                            <div className="text-xs text-muted-foreground mt-1">
                                Total seeds in this delivery as shown on the packet (seed inputs only)
                            </div>
                        </Field>

                        <Field>
                            <FieldLabel htmlFor="receivedQuantity">
                                Quantity {selectedInput && `(${selectedInput.unitOfMeasureDisplay || selectedInput.unitOfMeasure})`} <span className="text-red-500">*</span>
                            </FieldLabel>
                            <Input
                                id="receivedQuantity"
                                type="number"
                                step="0.01"
                                value={lineItem.receivedQuantity}
                                onChange={(e) => handleLineItemChange("receivedQuantity", e.target.value)}
                                disabled={isPending}
                                readOnly={hasPackCalculationInputs}
                            />
                            {hasPackCalculationInputs && (
                                <div className="text-xs text-muted-foreground mt-1">
                                    {isDiscreteUnit
                                        ? `${lineItem.packsReceived} packs = ${calculatedQuantity} ${selectedInput?.unitOfMeasureDisplay || "units"}`
                                        : `Auto from packs: ${lineItem.packsReceived} x ${lineItem.packSizePerPack} = ${calculatedQuantity}`
                                    }
                                </div>
                            )}
                            {fieldErrors.receivedQuantity && (
                                <div className="text-red-500 text-xs mt-1">
                                    {fieldErrors.receivedQuantity.join(", ")}
                                </div>
                            )}
                        </Field>

                        <Field>
                            <FieldLabel htmlFor="unitCost">
                                Cost Per Pack <span className="text-red-500">*</span>
                            </FieldLabel>
                            <Input
                                id="unitCost"
                                type="number"
                                step="0.01"
                                value={lineItem.unitCost}
                                onChange={(e) => handleLineItemChange("unitCost", e.target.value)}
                                disabled={isPending}
                            />
                            {fieldErrors.unitCost && (
                                <div className="text-red-500 text-xs mt-1">
                                    {fieldErrors.unitCost.join(", ")}
                                </div>
                            )}
                        </Field>
                    </div>

                    {/* First-receipt unit picker — only shown when the catalog row was
                        created without a native unit. Whatever the receiver picks here
                        becomes the Input's unit going forward. */}
                    {selectedInput && needsReceiptUnit(selectedInput.unitOfMeasure) && (
                        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-3">
                            <Field>
                                <FieldLabel htmlFor="lineUnitOfMeasure">
                                    Unit of Measure <span className="text-red-500">*</span>
                                </FieldLabel>
                                <Select
                                    value={lineItem.unitOfMeasure}
                                    onValueChange={(value) => handleLineItemChange("unitOfMeasure", value)}
                                    disabled={isPending || unitsQuery.isLoading}
                                >
                                    <SelectTrigger id="lineUnitOfMeasure" className="w-full">
                                        <SelectValue placeholder="Select unit (sets the Input's native unit)" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {unitsQuery.data?.map((u) => (
                                            <SelectItem key={u.value} value={u.name}>
                                                {u.name}{u.abbreviation ? ` (${u.abbreviation})` : ""}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground mt-1">
                                    This Input was created without a unit. Pick one — it'll become the native unit on the catalog from this first receipt onward.
                                </p>
                                {fieldErrors.unitOfMeasure && (
                                    <div className="text-red-500 text-xs mt-1">
                                        {fieldErrors.unitOfMeasure.join(", ")}
                                    </div>
                                )}
                            </Field>
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <Field>
                            <FieldLabel htmlFor="manufacturerBatchNumber">
                                Manufacturer Batch # <span className="text-red-500">*</span>
                            </FieldLabel>
                            <Input
                                id="manufacturerBatchNumber"
                                value={lineItem.manufacturerBatchNumber}
                                onChange={(e) => handleLineItemChange("manufacturerBatchNumber", e.target.value)}
                                disabled={isPending}
                            />
                            {fieldErrors.manufacturerBatchNumber && (
                                <div className="text-red-500 text-xs mt-1">
                                    {fieldErrors.manufacturerBatchNumber.join(", ")}
                                </div>
                            )}
                        </Field>

                        <Field>
                            <FieldLabel htmlFor="lotNumber">
                                Lot Number <span className="text-red-500">*</span>
                            </FieldLabel>
                            <Input
                                id="lotNumber"
                                value={lineItem.lotNumber}
                                onChange={(e) => handleLineItemChange("lotNumber", e.target.value)}
                                disabled={isPending}
                            />
                            {fieldErrors.lotNumber && (
                                <div className="text-red-500 text-xs mt-1">
                                    {fieldErrors.lotNumber.join(", ")}
                                </div>
                            )}
                        </Field>

                        <Field>
                            <FieldLabel htmlFor="manufactureDate">
                                Manufacture Date <span className="text-red-500">*</span>
                            </FieldLabel>
                            <DatePicker
                                id="manufactureDate"
                                value={lineItem.manufactureDate}
                                onChange={(value) => handleLineItemChange("manufactureDate", value)}
                                disabled={isPending}
                            />
                            {fieldErrors.manufactureDate && (
                                <div className="text-red-500 text-xs mt-1">
                                    {fieldErrors.manufactureDate.join(", ")}
                                </div>
                            )}
                        </Field>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        <Field>
                            <FieldLabel htmlFor="expiryDate">
                                Expiry Date {selectedInput?.hasExpiryDate && <span className="text-red-500">*</span>}
                            </FieldLabel>
                            <DatePicker
                                id="expiryDate"
                                value={lineItem.expiryDate}
                                onChange={(value) => handleLineItemChange("expiryDate", value)}
                                disabled={isPending || !selectedInput?.hasExpiryDate}
                                placeholder={selectedInput?.hasExpiryDate ? "" : "Not required for this input"}
                            />
                            {fieldErrors.expiryDate && (
                                <div className="text-red-500 text-xs mt-1">
                                    {fieldErrors.expiryDate.join(", ")}
                                </div>
                            )}
                        </Field>
                    </div>

                    <Button
                        onClick={handleAddLineItem}
                        disabled={isPending}
                        className="gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        Add Item
                    </Button>
                </div>

                {/* Added Items Table */}
                {addedBatches.length > 0 && (
                    <div className="space-y-4">
                        <h3 className="font-semibold">
                            Items ({addedBatches.length})
                        </h3>
                        <div className="rounded border overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Input</TableHead>
                                        <TableHead>Mfg Batch #</TableHead>
                                        <TableHead>Lot #</TableHead>
                                        <TableHead>Quantity</TableHead>
                                        <TableHead>Unit Cost</TableHead>
                                        <TableHead>Mfg Date</TableHead>
                                        <TableHead>Expiry</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {addedBatches.map((batch) => {
                                        return (
                                            <TableRow key={batch.tempId}>
                                                <TableCell className="font-medium">
                                                    {batch.inputLabel}
                                                </TableCell>
                                                <TableCell className="text-sm">
                                                    {batch.manufacturerBatchNumber}
                                                </TableCell>
                                                <TableCell className="text-sm">
                                                    {batch.lotNumber}
                                                </TableCell>
                                                <TableCell className="text-sm">
                                                    {batch.receivedQuantity}
                                                    {batch.packsReceived && batch.packSize ? (
                                                        <span className="text-xs text-muted-foreground ml-2">
                                                            ({batch.packsReceived} x {batch.packSize})
                                                        </span>
                                                    ) : null}
                                                </TableCell>
                                                <TableCell className="text-sm">
                                                    KES {batch.unitCost.toLocaleString()}
                                                </TableCell>
                                                <TableCell className="text-sm">
                                                    {batch.manufactureDate ? new Date(batch.manufactureDate).toLocaleDateString() : "—"}
                                                </TableCell>
                                                <TableCell className="text-sm">
                                                    {batch.expiryDate ? new Date(batch.expiryDate).toLocaleDateString() : "—"}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleRemoveBatch(batch.tempId)}
                                                        disabled={isPending}
                                                    >
                                                        <Trash2 className="w-4 h-4 text-red-600" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                )}
            </div>

            {/* Notes & Submit */}
            {addedBatches.length > 0 && (
                <div className="rounded-lg border p-6 space-y-4">
                    <Field>
                        <FieldLabel>Notes</FieldLabel>
                        <Textarea
                            value={header.notes}
                            onChange={(e) => handleHeaderChange("notes", e.target.value)}
                            placeholder="Add any additional notes (optional)"
                        />
                    </Field>

                    <div className="flex gap-2 justify-end">
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setAddedBatches([]);
                                setHeader(initialHeader);
                                setLineItem(initialLineItem);
                                setChosenInput(null);
                                setInputSearchValue("");
                                setInputSearchTerm("");
                            }}
                            disabled={isPending}
                        >
                            Clear All
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={isPending}
                        >
                            {isPending ? "Submitting..." : "Submit Receipt"}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
